import { useEffect, useState } from 'react';

/**
 * useLuminateOnEvent — subscribes a component to a window CustomEvent and
 * returns a `luminate` boolean that flashes true for `durationMs` whenever
 * the event fires, then resets. Optional `match` filter accepts the event's
 * detail object and returns true to accept the signal (e.g. only fire when
 * detail.eventId matches the current page's eventId).
 *
 * Pair with the `.mtl-luminate` Tailwind/CSS class (see index.css) for the
 * gold pulse glow used across the platform's "command center" cards.
 *
 * Listens to the global event bus dispatched by DashboardLayout's notification
 * poller, e.g.:
 *   • mtl:client-message-new   → workspace chat card
 *   • mtl:payment-received     → finance card
 *   • mtl:lead-new             → leads pipeline
 *   • mtl:proposal-viewed      → proposal status card
 *   • mtl:proposal-approved    → proposal status card
 *   • mtl:planning-submitted   → planning module
 *   • mtl:staffing-update      → staffing pill / readiness
 *   • mtl:live-issue           → blocker banner
 *   • mtl:event-complete       → archive / wrap-up card
 *
 * @returns {boolean} true while the luminate animation should be active
 */
export default function useLuminateOnEvent(eventName, match, durationMs = 3200) {
  const [luminate, setLuminate] = useState(false);

  useEffect(() => {
    if (!eventName) return;
    const handler = (e) => {
      if (typeof match === 'function' && !match(e.detail || {})) return;
      setLuminate(true);
      const id = window.setTimeout(() => setLuminate(false), durationMs);
      return () => window.clearTimeout(id);
    };
    window.addEventListener(eventName, handler);
    return () => window.removeEventListener(eventName, handler);
  }, [eventName, match, durationMs]);

  return luminate;
}
